// Gemeinsame Tx-Row Komponente für Mobile-Card (WalletScreen, AssetDetailScreen)
// und Desktop-Tabellenzeile (WalletDesktopTable).
//
// Visuelle Sprache 1:1 erhalten — alle Berechnungen kommen aus buildTxView
// (src/utils/txView.js).

import React, { useState } from 'react';
import { useTheme, useUI } from '../context/ThemeContext';
import { useCurrency } from '../context/CurrencyContext';
import { useT } from '../i18n/useT';
import { F, DUR } from '../styles/theme';
import { resolveTypColor, coinFullName, txTypeTag } from '../utils/txView';
import { fmt, fmtC } from '../utils/format';
import CoinImage from './CoinImage';
import Private from './Private';
import ExchangeLogo from './ExchangeLogo';

const FLOW_ARROW_BLUE = '#60A5FA';
const UNMATCHED_BORDER = '#F5A623';

// ── Sub-Components ───────────────────────────────────────────────────

function FlowArrow({ color, size = 14 }) {
  // size=14 für Mobile-Headline (display-Font), size=11 für Sub-Line (kleiner)
  const w = size;
  const h = Math.round(size * 9 / 14);
  return (
    <svg width={w} height={h} viewBox="0 0 26 10" fill="none"
      stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0, opacity: color === FLOW_ARROW_BLUE ? 1 : 0.7 }}>
      <path d="M1 5h24M19 1l6 4-6 4"/>
    </svg>
  );
}

function ExternalWalletIcon({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none"
      stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0, opacity: 0.75 }}>
      <path d="M6 3H3.5A1.5 1.5 0 0 0 2 4.5v8A1.5 1.5 0 0 0 3.5 14H10a1.5 1.5 0 0 0 1.5-1.5V11"/>
      <path d="M14 2H8M14 2v6M14 2L7 9"/>
    </svg>
  );
}

// Suffix "Wallet" am Ende abschneiden: "Phantom Wallet" → "Phantom",
// "Ledger Wallet" → "Ledger". Wird sowohl für Logo-Lookup als auch für
// das angezeigte Label benutzt — keine abgeschnittenen "Phantom Walle..."
// in der Quelle-Cell mehr.
function stripWalletSuffix(name) {
  return String(name || '').trim().replace(/\s*Wallet$/i, '');
}

// Plattform-Badge: zeigt das Original-Logo der Börse/Wallet via ExchangeLogo.
// Fallback (brand-farbener Initial-Block) liegt bereits in ExchangeLogo.
function PlatformBadge({ name, T: _T, size = 16 }) {
  return <ExchangeLogo name={stripWalletSuffix(name)} size={size} />;
}

// Farbiges Typ-Glyph-Badge unten rechts am Coin — EIN Marker für jeden Typ
// (Kauf/Verkauf/Tausch/Gesendet/Empfangen/Transfer/Staking/…). Quelle = txTypeTag
// (Wort + Farbe + iconPath). Identisch zur TransferChainCard. Dust = gedämpft.
function TypeGlyphBadge({ tag, T, size = 20 }) {
  if (!tag?.iconPath) return null;
  const g = Math.round(size * 0.62);
  return (
    <span style={{
      position: 'absolute', right: -3, bottom: -3,
      width: size, height: size, borderRadius: '50%',
      background: tag.dust ? (T.mute || '#9A9DA6') : tag.color,
      border: `2px solid ${T.bg}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <svg width={g} height={g} viewBox="0 0 24 24" fill="none"
        stroke="#fff" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
        <path d={tag.iconPath} />
      </svg>
    </span>
  );
}


// Renders "fromName → toName" inline. Wird sowohl im Mobile-Headline-Slot
// als auch im Desktop-Quelle-Cell verwendet.
function FlowInline({ flow, T, font = 'display', maxNameWidth = 110, withLogos = false, size = 'md' }) {
  if (!flow) return null;
  const arrowColor = flow.arrowColor || T.sub;
  const fontFamily = font === 'display' ? F.display : F.family;
  const fontSize = size === 'sm' ? 12 : 14;
  const fontWeight = size === 'sm' ? 500 : 600;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontFamily, fontSize, fontWeight,
      letterSpacing: font === 'display' ? '-0.02em' : '-0.005em',
      minWidth: 0, color: T.text,
    }}>
      {withLogos && <PlatformBadge name={flow.fromName} T={T} />}
      <span style={{
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        maxWidth: maxNameWidth,
        color: flow.fromMuted ? T.sub : undefined,
      }}>{stripWalletSuffix(flow.fromName)}</span>
      <FlowArrow color={arrowColor} size={font === 'display' ? 14 : 11} />
      {withLogos && <PlatformBadge name={flow.toName} T={T} />}
      <span style={{
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        maxWidth: maxNameWidth,
        color: flow.toMuted ? T.sub : undefined,
        display: 'inline-flex', alignItems: 'center', gap: 4,
      }}>
        {flow.showToExtIcon && <ExternalWalletIcon />}
        {stripWalletSuffix(flow.toName)}
      </span>
    </span>
  );
}

// Vorzeichen + Farbe für signierte Werte (Futures-PnL/Funding-Fee). Liefert
// { color, sign, num } — bei valueKind=null bleibt color null (Erbe) und num
// der rohe Wert. Hält Desktop-Tabelle und Mobile-Card identisch (TX-Parität).
function resolveSignedValue(view, T) {
  switch (view.valueKind) {
    case 'gain': return { color: T.green, sign: '+', num: Math.abs(view.displayValueEur) };
    case 'loss': return { color: T.red,   sign: '−', num: Math.abs(view.displayValueEur) };
    case 'zero': return { color: T.muted || T.mute, sign: '', num: 0 };
    default:     return { color: null,    sign: '',  num: view.displayValueEur };
  }
}

// ── variant="card" — Calm Tages-Feed-Zeile (WalletScreen, AssetDetailScreen) ─
// Klartext statt Tabelle: Coin-Token (mit dezenter Richtungs-Plakette) +
// Beschreibung („Bitcoin gekauft") + Quelle·Zeit links · Menge + €-Wert rechts.
// Zugänge (Kauf/Income/Eingang) dezent grün, alles andere neutral-warm.

const CARD_FIAT_NAME = { EUR: 'Euro', USD: 'US-Dollar', GBP: 'Pfund', CHF: 'Franken' };

// Typ-Zeichen je Transaktionsart (Bild-1-System). Stroke-Glyphen = Linie,
// (Frühere Coin-Eck-Plakette `TypeBadge` + `TYPE_GLYPHS` entfernt — die neue
//  Listen-Optik trägt das Typsignal über das farbige Typ-Wort + Wasserzeichen.)

function CardVariant({ view, onClick, onEdit, onDelete, expandable = false, rowRadius, rowShadow, marketData, flat }) {
  const { T } = useTheme();
  const ui = useUI();
  const { convert, symbol, locale } = useCurrency();
  const { t } = useT();
  const tx = view.raw || {};
  const [expanded, setExpanded] = useState(false);

  // Typ-Identität (Name + Farbe + Zeichen) aus dem zentralen Resolver — EINE
  // Quelle, die im ganzen Feed durchschlägt (Bild-1-System).
  const tag = txTypeTag(view);
  const fiatName = (sym) => CARD_FIAT_NAME[String(sym || '').toUpperCase()] || coinFullName(sym);
  const isFiatCoin = view.isTransfer && !!view.isFiatTransfer;
  // Betrag-Vorzeichen + Farbe: Zugang grün (+), Abgang rot (−), gleichwertig
  // (Tausch) neutral. Gebühr/Verlust/Funding zählen als Abgang.
  const isInflow  = view.isBuy || view.isIncome || (view.isTransfer && view.isTransferIn);
  const isOutflow = view.isSell || (view.isTransfer && !view.isTransferIn)
    || view.typKey === 'gebuehr' || view.typKey === 'loss' || view.typKey === 'funding_fee';
  const flow = isInflow ? 'in' : isOutflow ? 'out' : null;

  // Coin-Token links. NUR Tausch = Doppel-Icon (beide Coins, weggetauscht →
  // erhalten). Kauf/Verkauf/Income UND Transfers (Ein-/Ausgang) = EIN Coin-Logo +
  // Richtungs-Plakette; die Börsen-Bewegung (von → zu) steht in der Sub-Zeile —
  // identisch zur Wallet-Kette der übrigen Cards.
  const coinSize = ui.isMobile ? 40 : 38;
  const dualSize = ui.isMobile ? 30 : 29;
  const isDual = view.isSwap;
  let imageBlock;
  if (isDual) {
    imageBlock = (
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
        <CoinImage sym={view.primaryCoin} marketData={marketData} size={dualSize} />
        <FlowArrow color={T.sub} size={14} />
        <CoinImage sym={view.secondaryCoin} marketData={marketData} size={dualSize} />
        <TypeGlyphBadge tag={tag} T={T} />
      </div>
    );
  } else {
    // Coin-Logo + farbiges Typ-Glyph-Badge unten rechts (EIN Marker je Typ).
    imageBlock = (
      <div style={{ position: 'relative', flexShrink: 0, display: 'flex' }}>
        <CoinImage sym={view.primaryCoin} marketData={marketData} size={coinSize} />
        <TypeGlyphBadge tag={tag} T={T} />
      </div>
    );
  }

  // Kopf: Coin-Name (Klartext-Sätze raus — „zu viel", User 2026-06-11). Der Typ
  // steht als farbiges Wort daneben (tag.label). Fiat → ausgeschriebener Name.
  const headlineCoin = isFiatCoin ? fiatName(view.primaryCoin) : coinFullName(view.primaryCoin);

  // Meta (Quelle · Zeit).
  const zeit = String(tx.zeit || '').slice(0, 5);
  const timeStr = /^\d{2}:\d{2}$/.test(zeit) ? `${zeit} Uhr` : '';
  const metaStr = [view.exchangeName, timeStr].filter(Boolean).join(' · ');

  // Bewegungs-Kette in der Sub-Zeile (z.B. „Coinbase → Ledger", „MoonPay →
  // Ledger"): bei Transfers (Ein-/Ausgang) die Börsen-Bewegung, bei Kauf/Income
  // mit Liefer-Wallet die Wallet-Kette. NUR Tausch hat keine (zeigt beide Coins
  // bereits im Doppel-Logo). So sehen Transfers aus wie die übrigen Cards.
  const walletFlow = (!view.isSwap && view.flow) ? view.flow : null;

  // Rechte Seite — Menge (oben) + €-Wert/Klartext (unten).
  // Futures (valueKind) haben keine Coin-Menge → signierter €-Wert führt.
  let amountNode, secondNode;
  if (view.valueKind) {
    const sv = resolveSignedValue(view, T);
    amountNode = <span style={{ color: sv.color || T.text }}>{sv.sign}<Private>{`${fmt(convert(sv.num), locale)} ${symbol}`}</Private></span>;
    secondNode = <span style={{ color: T.sub }}>{view.typLabel.toLowerCase()}</span>;
  } else if (view.isSwap) {
    amountNode = <span style={{ color: T.text }}><Private>{fmtC(tx.mengeOut)}</Private> {tx.coinOut}</span>;
    secondNode = <span style={{ color: T.sub }}>→ <Private>{fmtC(tx.mengeIn)}</Private> {tx.coinIn}</span>;
  } else {
    // Zahlen rechts: grün bei +, rot bei − (User 2026-06-11). Gleichwertige
    // (Tausch → eigener Zweig oben; gematchter Transfer → TransferChainCard)
    // bleiben neutral.
    const sign = flow === 'in' ? '+' : flow === 'out' ? '−' : '';
    const amtColor = flow === 'in' ? (T.gain || T.green) : flow === 'out' ? (T.loss || T.red) : T.text;
    amountNode = <span style={{ color: amtColor }}>{sign}<Private>{view.mengeLabel}</Private></span>;
    if (isFiatCoin) {
      // Fiat: Menge IST der €-Wert + Typ steht oben → keine Dopplung unten.
      secondNode = null;
    } else if (Number(view.displayValueEur) > 0) {
      secondNode = (
        <span style={{ color: T.sub }}>
          {view.valueIsDerived && (
            <span style={{ color: T.mute, marginRight: 2 }} title={t('tx_derived_value_tip')}>~</span>
          )}
          <Private>{`${fmt(convert(view.displayValueEur), locale)} ${symbol}`}</Private>
        </span>
      );
    } else {
      // Kein bekannter €-Wert (alter Transfer ohne Preis) → leere Zeile statt
      // „0,000000 €", hält den Calm-Feed ruhig.
      secondNode = null;
    }
  }

  // Rahmen/Trenner sitzen auf dem OUTER-Container, Padding auf der Klick-Zeile —
  // so kann der Aufklapp-Bereich darunter im selben Rahmen sitzen (Calm-Feed).
  const outerStyle = flat
    ? {
        background: 'transparent',
        borderLeft: view.isUnmatched ? `3px solid ${UNMATCHED_BORDER}` : '3px solid transparent',
        borderBottom: `1px solid ${T.lineSoft || T.line}`,
        borderRadius: 0,
      }
    : {
        background: T.surface,
        border: `1px solid ${T.line || T.border}`,
        borderLeft: view.isUnmatched ? `3px solid ${UNMATCHED_BORDER}` : `1px solid ${T.line || T.border}`,
        borderRadius: ui.isMobile ? ui.radius.card : rowRadius,
        boxShadow: rowShadow || 'none',
        overflow: 'hidden',
      };
  const rowPad = flat ? '14px 4px 14px 10px' : (ui.isMobile ? '14px 16px' : '12px 14px');

  // Aufklapp-Modus: nur wenn der Aufrufer Edit/Delete reicht. Sonst Klassik
  // (Tap = onClick, kein Chevron) — Auswahl-/Link-Modi bleiben unverändert.
  const expandMode = !!(expandable && (onEdit || onDelete));
  const handleRowClick = () => { if (expandMode) setExpanded(e => !e); else onClick?.(); };

  const shortAddr = (a) => { const s = String(a || '').trim(); return s.length > 16 ? `${s.slice(0, 6)}…${s.slice(-4)}` : s; };
  const copyAddr = (e, a) => { e.stopPropagation(); try { navigator.clipboard?.writeText(String(a || '')); } catch { /* noop */ } };
  const isExtName = (n) => /extern|external|bank/i.test(String(n || ''));

  const actionStrip = (
    <div style={{ display: 'flex', borderTop: `1px solid ${T.lineSoft || T.line}` }}>
      <button onClick={(e) => { e.stopPropagation(); onEdit?.(tx); }}
        style={{ flex: 1, padding: '11px 0', background: 'none', border: 'none', borderRight: `1px solid ${T.lineSoft || T.line}`, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: T.text, fontFamily: F.family }}>
        {t('tx_edit')}
      </button>
      <button onClick={(e) => { e.stopPropagation(); onDelete?.(tx.id || tx._docId); }}
        style={{ flex: 1, padding: '11px 0', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: T.red, fontFamily: F.family }}>
        {t('tx_delete')}
      </button>
    </div>
  );

  // Ein „Von"/„An"-Bein im Aufklapp-Bereich: Richtung (farbig) · Plattform/Extern ·
  // optional die On-Chain-Adresse (kopierbar).
  const TransferLeg = ({ dir, dirColor, name, addr, ext }) => (
    <div style={{ padding: '10px 14px', borderTop: `1px solid ${T.lineSoft || T.line}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: dirColor, fontFamily: F.family }}>{dir}</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: T.sub, fontFamily: F.family }}>
          {ext ? <ExternalWalletIcon /> : <PlatformBadge name={name} T={T} size={14} />}
          {ext ? t('tx_external_short') : stripWalletSuffix(name)}
          {!ext && tx.ownWalletTransfer && <span style={{ color: T.mute }}>· {t('tx_own_wallet_suffix')}</span>}
        </span>
      </div>
      {addr && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
          <span onClick={(e) => copyAddr(e, addr)} title={addr}
            style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, color: T.sub, background: T.surfaceHi || T.surface, border: `1px solid ${T.lineSoft || T.line}`, borderRadius: 7, padding: '3px 7px', cursor: 'pointer' }}>
            {shortAddr(addr)}
          </span>
        </div>
      )}
    </div>
  );

  let expandBody = null;
  if (expandMode) {
    if (view.isTransfer && view.flow) {
      const fromExt = isExtName(view.flow.fromName);
      const toExt = view.flow.showToExtIcon || isExtName(view.flow.toName);
      expandBody = (
        <div>
          {tx.ownWalletTransfer && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 14px', fontSize: 11.5, color: T.sub, background: T.surfaceHi || T.surface, fontFamily: F.family }}>
              {tag.iconPath && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={tag.color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d={tag.iconPath} /></svg>}
              {t('tx_internal_transfer')}
            </div>
          )}
          <TransferLeg dir={t('tx_from')} dirColor={view.isTransferIn ? tag.color : T.sub} name={view.flow.fromName} addr={tx.chainFrom} ext={fromExt} />
          <TransferLeg dir={t('tx_to')} dirColor={view.isTransferIn ? T.sub : tag.color} name={view.flow.toName} addr={tx.chainTo} ext={toExt} />
          {(view.mengeLabel || tx.hash || tx.gebuehr > 0) && (
            <div style={{ padding: '8px 14px', fontSize: 11, color: T.mute, fontFamily: F.family, borderTop: `1px solid ${T.lineSoft || T.line}` }}>
              <Private>{view.mengeLabel}</Private>
              {tx.gebuehr > 0 && <> · {t('tx_fee_short')} <Private>{`${fmt(convert(tx.gebuehr), locale)} ${symbol}`}</Private></>}
              {tx.hash && <> · #{String(tx.hash).slice(0, 12)}…</>}
            </div>
          )}
          {actionStrip}
        </div>
      );
    } else {
      // Nicht-Transfer (Kauf/Verkauf/Tausch/Income/Futures/…): Coin-Name (hier
      // sichtbar, da Headline = Typ) + Datum + Gebühr + Notiz, dann Aktionen.
      expandBody = (
        <div>
          <div style={{ padding: '10px 14px', borderTop: `1px solid ${T.lineSoft || T.line}`, fontSize: 12, color: T.sub, fontFamily: F.family }}>
            <b style={{ color: T.text }}>{view.isSwap ? `${tx.coinOut} → ${tx.coinIn}` : headlineCoin}</b>
            {tx.datum && <> · {tx.datum}</>}
            {tx.gebuehr > 0 && <> · {t('tx_fee_short')} {fmt(convert(tx.gebuehr), locale)} {symbol}</>}
            {tx.notiz && tx.notiz !== 'CSV Import' && <> · {tx.notiz}</>}
          </div>
          {actionStrip}
        </div>
      );
    }
  }

  return (
    <div style={outerStyle}>
      <div
        onClick={handleRowClick}
        style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: rowPad, cursor: 'pointer', color: T.text,
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {imageBlock}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Titel = Typ-Wort (farbig). Coin über Avatar + Betrag + Aufklappen. */}
          <div style={{
            fontFamily: F.family, fontSize: 14.5, fontWeight: 700,
            letterSpacing: '-0.01em', color: tag.dust ? T.sub : tag.color,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {tag.label || headlineCoin}
          </div>
          <div style={{
            fontSize: 12.5, color: T.sub, marginTop: 3,
            fontFamily: F.family, letterSpacing: '-0.005em',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            display: 'flex', alignItems: 'center', gap: 6, minWidth: 0,
          }}>
            {walletFlow ? (
              <>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {stripWalletSuffix(walletFlow.fromName)}
                </span>
                <FlowArrow color={T.sub} size={11} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {stripWalletSuffix(walletFlow.toName)}
                </span>
                {timeStr && <span style={{ flexShrink: 0 }}>· {timeStr}</span>}
              </>
            ) : metaStr}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 8 }}>
          <div style={{
            fontFamily: F.num || F.mono, fontVariantNumeric: 'tabular-nums',
            fontSize: 14.5, fontWeight: 600, letterSpacing: '-0.01em',
          }}>
            {amountNode}
          </div>
          <div style={{
            fontFamily: F.family, fontSize: 12.5, marginTop: 3,
            letterSpacing: '-0.005em',
          }}>
            {secondNode}
          </div>
        </div>
        {expandMode && (
          <span aria-hidden style={{
            flexShrink: 0, marginLeft: 2, display: 'inline-flex', color: T.mute,
            transform: expanded ? 'rotate(180deg)' : 'none',
            transition: `transform ${DUR.fast}ms ease`,
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
          </span>
        )}
      </div>
      {expandMode && expanded && expandBody}
    </div>
  );
}

// ── variant="table" — Desktop (WalletDesktopTable) ──────────────────

function TableVariant({ view, marketData, onEdit, hovered, onMouseEnter, onMouseLeave, columns, minWidth, isLast }) {
  const { T } = useTheme();
  const { convert, symbol, locale } = useCurrency();
  const { t } = useT();

  const typColor = resolveTypColor(view, T);
  const RED = T.red;

  // Asset-Cell: Swap = beide Logos, sonst 1 Logo + Symbol-Text
  const assetCell = view.isSwap ? (
    <div style={{
      padding: '11px 14px',
      display: 'flex', alignItems: 'center', gap: 10,
      color: T.text, fontSize: 13.5, fontWeight: 500,
      letterSpacing: '-0.01em', overflow: 'hidden',
    }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
        <CoinImage sym={view.primaryCoin} marketData={marketData} size={20} />
        <span style={{ color: T.sub, fontSize: 12, margin: '0 2px' }}>›</span>
        <CoinImage sym={view.secondaryCoin} marketData={marketData} size={20} />
      </span>
      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {view.primaryCoin} → {view.secondaryCoin}
      </span>
    </div>
  ) : (
    <div style={{
      padding: '11px 14px',
      display: 'flex', alignItems: 'center', gap: 10,
      color: T.text, fontSize: 13.5, fontWeight: 500,
      letterSpacing: '-0.01em', overflow: 'hidden',
    }}>
      <CoinImage sym={view.primaryCoin} marketData={marketData} size={22} />
      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {view.primaryCoin}
      </span>
    </div>
  );

  // Quelle-Cell: flow → fromName → toName mit Plattform-Badges, sonst exchangeName
  const quelleCell = view.flow ? (
    <div style={{
      padding: '11px 14px',
      fontSize: 12, color: T.text, letterSpacing: '-0.005em',
      overflow: 'hidden', whiteSpace: 'nowrap',
      display: 'flex', alignItems: 'center', gap: 6,
    }}>
      <PlatformBadge name={view.flow.fromName} T={T} size={16} />
      <span style={{
        overflow: 'hidden', textOverflow: 'ellipsis',
        color: view.flow.fromMuted ? T.sub : T.text,
        maxWidth: 80,
      }}>{stripWalletSuffix(view.flow.fromName)}</span>
      <FlowArrow color={view.flow.isTransferFlow ? FLOW_ARROW_BLUE : T.sub} size={11} />
      <PlatformBadge name={view.flow.toName} T={T} size={16} />
      <span style={{
        overflow: 'hidden', textOverflow: 'ellipsis',
        color: view.flow.toMuted ? T.sub : T.text,
        maxWidth: 80,
        display: 'inline-flex', alignItems: 'center', gap: 3,
      }}>
        {view.flow.showToExtIcon && <ExternalWalletIcon />}
        {stripWalletSuffix(view.flow.toName)}
      </span>
    </div>
  ) : (
    <div style={{
      padding: '11px 14px',
      fontSize: 12, color: T.sub, letterSpacing: '-0.005em',
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    }}>
      {view.exchangeName}
    </div>
  );

  return (
    <div
      onClick={() => onEdit?.(view.raw)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        display: 'grid',
        gridTemplateColumns: columns,
        minWidth: minWidth,
        borderBottom: isLast ? 'none' : `1px solid ${T.lineSoft || T.line}`,
        borderLeft: view.isUnmatched
          ? `2px solid ${UNMATCHED_BORDER}`
          : hovered ? `2px solid ${RED}` : '2px solid transparent',
        background: hovered ? T.itemHover : 'transparent',
        cursor: 'pointer',
        transition: `background ${DUR.fast}ms, color ${DUR.fast}ms`,
        alignItems: 'center',
      }}
    >
      {/* Typ */}
      <div style={{
        padding: '11px 14px',
        fontFamily: F.family, fontSize: 10,
        fontWeight: 700, letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: typColor,
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        {view.typLabel}
        {view.isScamDust && (
          <span title={t('tx_dust_tip')} style={{
            fontSize: 8.5, fontWeight: 700, letterSpacing: '0.08em',
            color: T.mute, border: `1px solid ${T.line || T.border}`,
            borderRadius: 3, padding: '1px 4px',
          }}>
            {t('tx_dust_badge')}
          </span>
        )}
      </div>
      {/* Asset */}
      {assetCell}
      {/* Quelle / Flow */}
      {quelleCell}
      {/* Datum — zwischen Quelle und Menge */}
      <div style={{
        padding: '11px 14px',
        fontFamily: F.num || F.mono,
        fontVariantNumeric: 'tabular-nums',
        fontSize: 12.5, color: T.sub, letterSpacing: '-0.005em',
      }}>
        {view.datum}
      </div>
      {/* Menge — links-ausgerichtet, mehr Platz für Swap-Strings */}
      <div style={{
        padding: '11px 14px', textAlign: 'left',
        fontFamily: F.num || F.mono,
        fontVariantNumeric: 'tabular-nums',
        fontSize: 12.5, color: T.text,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        <Private>{view.mengeLabel}</Private>
      </div>
      {/* Wert */}
      {(() => {
        const sv = resolveSignedValue(view, T);
        return (
          <div style={{
            padding: '11px 14px', textAlign: 'right',
            fontFamily: F.num || F.mono,
            fontVariantNumeric: 'tabular-nums',
            fontSize: 13, fontWeight: 600, color: sv.color || T.text,
            letterSpacing: '-0.01em',
          }}>
            {view.valueIsDerived && (
              <span style={{ color: T.mute, marginRight: 2 }} title={t('tx_derived_value_tip')}>~</span>
            )}
            {sv.sign}<Private>{fmt(convert(sv.num), locale)}</Private> <span style={{ opacity: 0.5 }}>{symbol}</span>
          </div>
        );
      })()}
    </div>
  );
}

// ── Public ──────────────────────────────────────────────────────────

export default function TxRow(props) {
  const { variant = 'card' } = props;
  if (variant === 'table') return <TableVariant {...props} />;
  return <CardVariant {...props} />;
}

export { FlowArrow, ExternalWalletIcon, PlatformBadge, FlowInline };
